import Shift from "app/models/shift"
import Timeslot from "app/models/timeslot"
import { formatMidnightToNextDay } from "app/utils"
import ColorUtil from "color"
import moment, { Moment } from "moment"

const weekDays = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]

export const isEmpty = (value: any) =>
  value === null ||
  value === "" ||
  typeof value === "undefined" ||
  (typeof value === "object" && Object.values(value).length === 0)

export const { timeZone } = Intl.DateTimeFormat().resolvedOptions()

export const toLocalDateTime = (date: Date) => {
  if (isEmpty(date)) return
  return new Date(date).toLocaleString("nl", {
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    month: "2-digit",
    year: "2-digit",
  })
}

export const toLocalTime = (date: Date) => {
  if (isEmpty(date)) return
  return new Date(date).toLocaleString("nl", {
    hour: "2-digit",
    minute: "2-digit",
  })
}

export const toLocalDate = (date: Date) => {
  if (isEmpty(date)) return
  return new Date(date).toLocaleString("nl", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  })
}

export const birthdayDate = (date: string) => {
  if (isEmpty(date)) return
  return new Date(date).toLocaleString("nl", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })
}

export const getRandomHexColor = (): string => {
  const randomColor = Math.floor(Math.random() * 16777215).toString(16).padStart(6, "0")
  return `#${randomColor}`
}

export const brighten = (color: string, brightness: number) =>
  ColorUtil({
    h: ColorUtil(color).hsv().round().object().h,
    s: ColorUtil(color).hsv().round().object().s,
    v: ColorUtil(color).hsv().round().object().v + brightness,
  }).string()

export const opacify = (color: string, opacity: number) =>
  ColorUtil([ColorUtil(color).red(), ColorUtil(color).green(), ColorUtil(color).blue(), opacity]).string()

export function isAccessTokenExpired(accessToken: any): boolean {
  const expirationDate = accessToken.exp * 1000
  if (
    accessToken["https://ppt/activeGroup"].system_roles &&
    accessToken["https://ppt/activeGroup"].system_roles.includes("Tester")
  )
    return false
  return isExpired(expirationDate)
}

export function isExpired(date: number, today = Date.now() - 2000) {
  return date <= today
}

export function parseToken(t: string) {
  if (typeof t !== "string") {
    return
  }
  const base64Url = t.split(".")[1]
  const base64 = base64Url.replace("-", "+").replace("_", "/")
  return JSON.parse(window.atob(base64))
}

export const formatDateTime = (dateTime: string | Moment): Moment => {
  if (moment.utc(dateTime).isValid()) {
    return moment.utc(dateTime)
  }
  return moment.utc(dateTime, "HH:mm")
}

export function getDaysInMonth(month: number, year: number): number[] {
  const date = new Date(year, month)
  const days = Array.from({ length: moment(date).daysInMonth() }, (x, i) =>
    Number(moment(date).startOf("month").add(i, "days").format("d").toLowerCase()),
  )
  return days
}

export const capitalizeWord = (word: string) => word.charAt(0).toUpperCase() + word.slice(1)

export const getCurrentMonth = () => new Date().getMonth()

export const getCurrentYear = () => new Date().getFullYear()

export const getYear = (date: string) => moment(date).format("YYYY")

export function dateToUTC(date: Date) {
  date.setUTCHours(0)
  date.setUTCMinutes(0)
  date.setUTCSeconds(0)
  date.setUTCMilliseconds(0)
  return date
}

export const getDayFromNumber = (index: number) => weekDays[index]

export const RGBAToRGB = (RGBAcolor: string, background: string) => {
  const alpha = ColorUtil(RGBAcolor).alpha()

  return ColorUtil([
    (1 - alpha) * ColorUtil(background).red() + alpha * ColorUtil(RGBAcolor).red(),
    (1 - alpha) * ColorUtil(background).green() + alpha * ColorUtil(RGBAcolor).green(),
    (1 - alpha) * ColorUtil(background).blue() + alpha * ColorUtil(RGBAcolor).blue(),
  ]).string()
}

export const parseDate = (date: string) => moment.utc(date).format("DD/MM/YYYY")

export const startDate = (date: Moment) => date.clone().utc().startOf("isoWeek").toISOString()

export const endDate = (date: Moment) => date.clone().utc().endOf("isoWeek").toISOString()

// TODO:
// Rename to formatDateToTime / formatDateToTimeMoment / formatDateToLocalTime / formatDateToHours
// toHour also does not need date to be converted by .toISOString
export const toHour = (date: string | Moment) => moment.utc(date).format("HH:mm")
export const toHourMoment = (date: string | Moment) => moment.utc(date, "HH:mm")
export const toLocalHour = (date: string) => moment(date).format("HH:mm")
export const formatTimestamp = (hour: string) => moment(hour, "HH:mm:ss").format("HH:mm")
export const formatDateToMonthLocale = (date: string) => moment(date).format("DD MMMM YYYY")
export const formatWeekdayToShortLocale = (weekday: number) => moment().day(weekday).format("dd")
export const formatWeekdayToLongLocale = (weekday: number) => moment().day(weekday).format("dddd")
export const formatDateToLongLocale = (date: string | Moment) => moment(date).format("dddd DD MMMM YYYY")
export const getWeekdayFromDate = (date: string | Moment) => {
  const dateMoment = typeof date === "string" ? moment.utc(date) : date
  return dateMoment.isoWeekday() - 1
}

/**
 * Format time string (i.e. "HH:mm:ss") to HH:mm
 */
export const formatTimeString = (time: string) => moment.utc(time, "HH:mm")

export const dateToHumanReadable = (date: string) => moment.utc(date).format("D MMMM YYYY")

export function formatNumbersWithDecimalsToTime(value: number): { hours: number; minutes: number; negative: boolean } {
  const v = value >= 0 ? 1 : -1
  const negative = v === -1

  const roundedHours = v * Math.floor(Math.abs(value))
  const calculatedMinutes = (value - roundedHours) * 60
  const roundedMinutes = Math.round(calculatedMinutes)
  const areMinutesFullHour = Math.abs(roundedMinutes) === 60
  const hours = areMinutesFullHour ? Math.abs(roundedHours) + 1 : Math.abs(roundedHours)
  const minutes = areMinutesFullHour ? 0 : Math.abs(roundedMinutes)
  return {
    hours,
    minutes,
    negative,
  }
}

export const monthAndYearToString = (month: number, year: number) => {
  if (month < 0 || month > 11) return moment()
  return moment().utc().set("year", year).startOf("year").add(month, "months").startOf("month")
}

export function getYearRange(year: number) {
  const from = moment().utc().set("year", year).startOf("year").toISOString()
  const to = moment().set("year", year).utc().endOf("year").toISOString()

  return { to, from }
}

export function getMonthRange(value: string) {
  const date = moment.utc(value)
  const monthRange = {
    from: date.startOf("month").toISOString(),
    to: date.endOf("month").toISOString(),
  }
  return monthRange
}

export const calculateStartHour = (shifts: Shift[]) => {
  const momentsArray = makeMomentArray({ shifts, isStart: true })
  return moment.min(momentsArray)
}

export const calculateEndHour = (shifts: Shift[]) => {
  const momentsArray = makeMomentArray({ shifts, isStart: false })
  return moment.max(momentsArray)
}

export const makeMomentArray = (data: { shifts: Shift[]; isStart: boolean }) => {
  const { shifts, isStart } = data
  return shifts.map(item =>
    isStart ? toHourMoment(item.start_time) : toHourMoment(formatMidnightToNextDay(item.end_time)),
  )
}

export const checkIfDateIsToday = (date: Moment) => moment().isSame(date, "date")

const compareName = (name1: string, name2: string) => {
  if (name1 < name2) {
    return -1
  }
  if (name1 > name2) {
    return 1
  }
  return 0
}

export const compareStartTimes = (startTime1: string, startime2: string) => {
  const startTimeA = moment.utc(startTime1, "HH:mm:ss")
  const startTimeB = moment.utc(startime2, "HH:mm:ss")

  if (startTimeA.diff(startTimeB) > 0) {
    return 1
  }
  if (startTimeA.diff(startTimeB) < 0) {
    return -1
  }
  return 0
}

export function sortTimeslotsOnStartTime<T extends Timeslot>(timeslots: T[]): T[] {
  timeslots.sort((a: T, b: T): number => {
    const fullnameA = `${a.employee.first_name.toLowerCase()} ${b.employee.last_name.toLowerCase()}`
    const fullnameB = `${b.employee.first_name.toLowerCase()} ${b.employee.last_name.toLowerCase()}`
    const timeA = a.hours.start_time
    const timeB = b.hours.start_time

    // Sort on last name + first name as a string.
    // Then sort on start time.
    return compareName(fullnameA, fullnameB) || compareStartTimes(timeA, timeB)
  })
  return timeslots
}

export function arraysIntersect(arr1: unknown[], arr2: unknown[]): boolean {
  return arr1.some(item => arr2.includes(item))
}

export const convertToUTCDate = (date: Moment): Moment => {
  const format = "YYYY-MM-DD"
  return moment.utc(date.format(format), format)
}
